import type { Rng } from '../../core/rng/Rng';
import type { EventBus } from '../../core/EventBus';
import type { RunState } from '../run/RunState';
import type { World } from '../World';
import { ARTIFACT_DEFS, ARTIFACTS_BY_ID, BLESSED_BOON_MULT, type ArtifactProc } from './ArtifactDefs';
import { CURSE_POOL, CURSES_BY_ID } from './CurseDefs';

/** Proc chances per drop (tunable). Remainder rolls 'none'. */
export const PROC_CURSED_CHANCE = 0.15;
export const PROC_BLESSED_CHANCE = 0.10;

/**
 * Owns equipped artifacts for the run: applies quality-scaled stat modifiers
 * and subscribes event hooks (per-artifact, so removal is clean). Hooks
 * receive the CURRENT world (worlds are per-floor).
 */
export class ArtifactSystem {
  private world: World | null = null;
  private unsubs = new Map<string, (() => void)[]>();

  constructor(
    private readonly runState: RunState,
    private readonly events: EventBus,
  ) {}

  setWorld(world: World): void {
    this.world = world;
  }

  acquire(artifactId: string, quality: number, proc: ArtifactProc = 'none', curseId: string | null = null): void {
    const def = ARTIFACTS_BY_ID.get(artifactId);
    if (!def || this.runState.artifacts.some((a) => a.artifactId === artifactId)) return;
    this.runState.artifacts.push({ artifactId, quality, proc, curseId });
    // Quality scales BOONS only; blessed doubles each boon modifier on top.
    // Curse modifiers stay fixed — never scaled, never doubled — so a high
    // roll is strictly better. (Small flat maxHp boons barely move under
    // rounding; accepted wrinkle.)
    const boonMult = quality * (proc === 'blessed' ? BLESSED_BOON_MULT : 1);
    const modifiers = def.modifiers.map((m) => ({
      stat: m.stat,
      ...(m.flat !== undefined ? { flat: m.flat * boonMult } : {}),
      ...(m.pct !== undefined ? { pct: m.pct * boonMult } : {}),
    }));
    if (proc === 'cursed' && curseId !== null) {
      const curse = CURSES_BY_ID.get(curseId);
      if (curse) modifiers.push(...curse.modifiers.map((m) => ({ ...m })));
    }
    this.runState.stats.addModifiers(`artifact:${artifactId}`, modifiers);
    const subs: (() => void)[] = [];
    for (const hook of def.hooks ?? []) {
      subs.push(this.events.on(hook.event, (payload) => {
        if (this.world) hook.effect(this.world, payload as never);
      }));
    }
    this.unsubs.set(artifactId, subs);
    this.events.emit('artifactAcquired', { artifactId, proc, curseId, quality });
  }

  /** Unequip: reverse modifiers (boon + curse share one source), detach hooks. */
  remove(artifactId: string): void {
    const idx = this.runState.artifacts.findIndex((a) => a.artifactId === artifactId);
    if (idx < 0) return;
    this.runState.artifacts.splice(idx, 1);
    this.runState.stats.removeSource(`artifact:${artifactId}`);
    for (const u of this.unsubs.get(artifactId) ?? []) u();
    this.unsubs.delete(artifactId);
  }

  /** Replace an equipped artifact with a better-quality copy of the same id. */
  improve(artifactId: string, quality: number, proc: ArtifactProc = 'none', curseId: string | null = null): void {
    this.remove(artifactId);
    this.acquire(artifactId, quality, proc, curseId);
  }

  dispose(): void {
    for (const subs of this.unsubs.values()) for (const u of subs) u();
    this.unsubs.clear();
  }
}

/**
 * Pre-roll an artifact drop from the loot stream: uniform pick over the ids
 * not yet dropped this run (without replacement; null when exhausted).
 */
export function rollArtifactDrop(rng: Rng, alreadyDropped: Set<string>): string | null {
  const avail = ARTIFACT_DEFS.filter((d) => !alreadyDropped.has(d.id));
  if (avail.length === 0) return null;
  const picked = avail[rng.int(avail.length)]!;
  alreadyDropped.add(picked.id);
  return picked.id;
}

export interface ArtifactProcRoll {
  proc: ArtifactProc;
  curseId: string | null;
}

/**
 * Pre-roll a drop's proc from the loot stream: cursed adds one curse from the
 * shared pool (extra int draw only on cursed — proc outcome is itself
 * deterministic, so draw order stays fixed).
 */
export function rollArtifactProc(rng: Rng): ArtifactProcRoll {
  const r = rng.next();
  if (r < PROC_CURSED_CHANCE) {
    return { proc: 'cursed', curseId: CURSE_POOL[rng.int(CURSE_POOL.length)]!.id };
  }
  if (r < PROC_CURSED_CHANCE + PROC_BLESSED_CHANCE) return { proc: 'blessed', curseId: null };
  return { proc: 'none', curseId: null };
}
